<template>
  <div class="mt-3" v-if="!this.loading">
    <div v-if="!this.fundsFile" class="text-center top-text">
      {{ $t('setupNewDistribution') | capitalize }}
    </div>
    <div v-else class="text-center top-text">
      <span class="table-sub-class">
        {{ $t('editDistributionForFile') }}
      </span>
      <span>
        {{ this.fundsFile.path.split('/').pop() }}
      </span>
    </div>
    <div class="distribution-manager-container pb-2">
      <!-- TOP SECTION -->
      <div class="top-section">
        <div>
          <div
            class="icon"
            :class="{ current: isCurrentStep(1) }"
          >
            <i class="far fa-ballot" />
          </div>
          <div class="table-main-class mt-2 text-center">
            {{ $t('funds') | capitalize }}
          </div>
        </div>
        <div class="separator" />
        <div>
          <div
            class="icon"
            :class="{ current: isCurrentStep(2) }"
          >
            <i class="far fa-calendar-alt" />
          </div>
          <div class="table-main-class mt-2 text-center">
            {{ $t('time_period') | capitalize }}
          </div>
        </div>
        <div class="separator" />
        <div>
          <div
            class="icon"
            :class="{ current: isCurrentStep(3) }"
          >
            <i class="far fa-file-invoice-dollar" />
          </div>
          <div class="table-main-class mt-2 text-center">
            {{ $t('distribution') | capitalize }}
          </div>
        </div>
      </div>

      <!-- STEPS -->
      <div>
        <!-- STEP 1: Funds -->
        <FundsFileSelector v-if="isCurrentStep(1)" v-model="fundsFile" />

        <!-- STEP 2: Period -->
        <PeriodSelector v-if="isCurrentStep(2)" v-model="period" />

        <!-- STEP 3: Allocation Setup -->
        <AllocationSelector v-if="isCurrentStep(3)" :allocations="allocations" @change="setCurrentAllocations" :isDraft="isDraft" />
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end mr-4 mb-4">
        <b-button
          v-if="isCurrentStep(1)"
          size="lg"
          variant="primary"
          @click="createDistribution"
        >
          <span>{{ $t('continue') | capitalize }}</span>
        </b-button>

        <b-button
          v-if="isCurrentStep(2) && isDraft"
          :disabled="!isValidPeriod"
          size="lg"
          variant="primary"
          @click="setPeriod"
        >
          <span>{{ $t('continue') | capitalize }}</span>
        </b-button>

        <b-button
          v-if="isCurrentStep(3) && isDraft"
          size="lg"
          variant="primary"
          @click="saveAsDraft(true)"
        >
          <span>{{ $t('saveAsDraft') | capitalize }}</span>
        </b-button>

        <action-button
          v-if="isCurrentStep(3) && isDraft"
          size="lg"
          variant="outline-primary"
          class="ml-3"
          @click="saveAsPending"
        >
          <span>{{ $t('finishSetup') | capitalize }}</span>
        </action-button>

        <b-button
          v-if="isCurrentStep(3) && !isDraft && canEditDistribution
           && !editDistribution"
          size="lg"
          variant="primary"
          class="ml-3"
          @click="setEditable(true)"
        >
          <span>{{ $t('editDistribution') | capitalize }}</span>
        </b-button>

        <b-button
          v-if="isCurrentStep(3) && !isDraft"
          size="lg"
          variant="primary"
          class="ml-3"
          @click="$router.push({ name: 'distributions' })"
        >
          <span>{{ $t('go_back') | capitalize }}</span>
        </b-button>
      </div>
    </div>

    <b-modal
      id="playlistRematchModal"
      ref="playlistRematchModal"
      :title="$t('playlist_rematching') | capitalize"
      hide-footer
    >
      <p>
        {{ $t('playlist_rematching_desc') }}
      </p>

      <b-form-group class="mt-3">
        <b-form-checkbox v-model="rematchPlaylists">
          {{ $t('playlist_rematching_check') }}
        </b-form-checkbox>
      </b-form-group>

      <div class="d-flex justify-content-end mt-4">
        <b-button
          size="lg"
          variant="outline-secondary"
          @click="$refs.playlistRematchModal.hide()"
        >
          {{ $t('cancel') | capitalize }}
        </b-button>
        <action-button
            size="lg"
            variant="primary"
            class="ml-3"
            @click="confirmFinishSetup"
        >
         {{ $t('accept') | capitalize }}
        </action-button>
      </div>
    </b-modal>

  </div>
</template>

<style lang="scss" scoped>
.top-text {
  font-size: 22px;
  font-weight: 500;
  margin-top: 40px;
}

.distribution-manager-container {
  min-height: 450px;
  background-color: #eff3f5;
  max-width: 75%;
  min-width: 1200px;
  margin: auto;
}

.icon {
  font-size: 40px;
  background-color: #6b8fa2;
  text-align: center;
  padding-top: 12px;
  color: white;
  border-radius: 50px;
  height: 80px;
  width: 80px;
  opacity: 0.3;
}

.current {
  opacity: 1;
  background-color: #0091ff !important;
  color: white;
}

.separator {
  height: 3px;
  width: 80px;
  background-color: white;
  margin: 40px 15px 70px 15px;
}

.top-section {
  border-bottom: solid 2px white;
  width: 100%;
  height: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<script>

import ActionButton from "@/components/Modules/ActionButton"
import AllocationSelector from "./AllocationSelector.vue"
import FundsFileSelector from "./FundsFileSelector.vue"

import PeriodSelector from "./PeriodSelector.vue"
import { api } from "@/main"
import { get } from "lodash"
import { mapGetters } from "vuex"

export default {
  name: "DistributionManager",
  components: {
    ActionButton,
    FundsFileSelector,
    AllocationSelector,
    PeriodSelector,
  },
  computed: {
    ...mapGetters("soundsys", ["statusTypes"]),
    isDraft () {
      return Boolean(this.distribution) && this.distribution.status === this.statusTypes.DRAFT
    },
    canEditDistribution () {
      return Boolean(this.distribution) && [this.statusTypes.DRAFT, this.statusTypes.ERROR, "done"].includes(this.distribution.status)
    },
    isValidPeriod () {
      return this.period.length
    },
  },
  data () {
    return {
      fundsFile: null,
      period: [],
      allocations: [],
      currentAllocations: [],
      distribution: null,
      currentStep: 1,
      loading: false,
      editDistribution: false,
      rematchPlaylists: false,
    }
  },

  methods: {
    setEditable (editable){
      this.saveAsDraft(false)
      this.editDistribution = editable
    },
    isCurrentStep (step) {
      return this.currentStep == step
    },
    setCurrentStep (step) {
      this.currentStep = step
    },
    getDistribution (id) {
      return api.get(`distributions/${id}/`)
        .then(response =>  { this.distribution = response.data })
        .catch(error => {
          if (error.response.status === 404) {
            this.$router.replace({ name: "not-found" })
          }
        })
    },
    setData (distribution) {
      const { fund_file, start_date, end_date, latest_iteration } = distribution
      this.fundsFile = fund_file
      this.period = start_date && end_date ? [new Date(start_date), new Date(end_date)] : []
      this.allocations = latest_iteration.fund_allocations
    },
    datetimeToUTCDate (datetime) {
      // https://stackoverflow.com/questions/23593052/format-javascript-date-as-yyyy-mm-dd
      return this.toISODate(new Date(datetime.getTime() - (datetime.getTimezoneOffset() * 60000 )))
    },
    toISODate (datetime) {
      return datetime.toISOString().split("T")[0]
    },
    setCurrentAllocations (allocations) {
      this.currentAllocations = allocations
    },
    prepareAllocationsData (allocations) {
      return allocations.map(allocation => ({
        id: allocation.id,
        rule: get(allocation.rule, "rule", null),
        right_type: get(allocation.right_type, "id", null),
        deduction: allocation.deduction,
        playlist_sheets: allocation.playlist_sheets.map(sheet => sheet.id),
      }))
    },
    isSetupComplete (data) {
      return data.map(allocation => {
        return this.isAllocationComplete(allocation)
      }).every(complete => complete)
    },
    isAllocationComplete (allocation) {
      const isUPA = allocation.rule?.name === "upa"
      const isProRata = allocation.rule?.name === "pro-rata"

      const hasUseType = Boolean(allocation.right_type)
      const hasProRataFile = Boolean(allocation.pro_rata_file)
      const hasPlaylists = Boolean(allocation.playlist_sheets?.length)
      const hasUPAMembers = Boolean(allocation.upa_members?.length)

      const validUPA = isUPA && hasUPAMembers
      const validProRata = hasUseType && isProRata && hasProRataFile
      const validUsages = hasUseType && !isUPA && !isProRata && hasPlaylists

      return validUPA || validProRata || validUsages
    },
    createDistribution () {
      const data = {
        fund_file: this.fundsFile,
        status: this.statusTypes.DRAFT
      }

      api.post("distributions/", data)
        .then(response => {
          this.$router.replace({
            name: "distribution-detail",
            params: { id: response.data.id }
          })
        })
    },
    setPeriod () {
      const data = {
        fund_file: this.distribution.fund_file,
        start_date: this.datetimeToUTCDate(this.period[0]),
        end_date: this.datetimeToUTCDate(this.period[1]),
        status: this.statusTypes.DRAFT,
      }

      api.put(`distributions/${this.id}/`, data)
        .then(response => {
          this.distribution = response.data
          this.setCurrentStep(3)
        })
    },
    saveAsDraft (redirect) {
      this.saveDistribution(this.currentAllocations, this.statusTypes.DRAFT, redirect)
    },
    saveAsPending () {
      if (!this.isSetupComplete(this.currentAllocations)) {
        this.$toasted.global.toast(this.$t("all_fields_must_be_filled"))
      } else {
        this.rematchPlaylists = false
        this.$refs.playlistRematchModal.show()
      }
    },
    confirmFinishSetup () {
      this.$refs.playlistRematchModal.hide()
      this.saveDistribution(this.currentAllocations, this.statusTypes.PENDING, true)
    },
    saveDistribution (allocationsData, status, redirect) {
      let data = {
        fund_file: this.distribution.fund_file,
        start_date: this.toISODate(this.period[0]),
        end_date: this.toISODate(this.period[1]),
        status,
        latest_iteration: {
          fund_allocations: this.prepareAllocationsData(allocationsData)
        },
        rematch_playlists: this.rematchPlaylists
      }
      api.put(`distributions/${this.id}/`, data)
        .then(response => {
          this.loading = false
          this.distribution = response.data
          this.$toasted.global.toast(this.$t("distribution_saved"))
          if(redirect){
            this.$router.push({ name: "distributions" })
          }
          else{
            api.get(`distributions/${this.id}/`)
              .then(response => {
                this.distribution = response.data
              })
          }

        })
        .catch(error => {
          this.loading = false

          error.response.data?.latest_iteration.fund_allocations.filter(
            item => "non_field_errors" in item
          ).map(
            (item) => item.non_field_errors.map(err => err)
          ).forEach((item) => {this.$toasted.global.toast(item)})

        })
    },
  },
  props: {
    id: [String, Number],
  },
  watch: {
    distribution (distribution) {
      this.setData(distribution)
    }
  },
  async mounted () {
    // If a distribution ID is present as a router prop...
    if (this.id) {
      // ...fetch the data from the distribution...
      this.loading = true
      await this.getDistribution(this.id)
      // ...and set step depending on setup
      this.isValidPeriod ? this.setCurrentStep(3) : this.setCurrentStep(2)
      this.loading = false
    }
  },
}
</script>
